"use client";

import React from "react";
import { LocaleLink } from "@/components/shared/LocaleLink";
import { useTranslations } from "@/lib/i18n-client";
import { track } from "@/lib/analytics";
import { Zap, ArrowRight, ShieldCheck, Globe, Tag } from "lucide-react";
import HeroScene from "./HeroScene";

/**
 * eSIMCobra hero — deal/speed-first, brutalist. Activation message + dual CTA,
 * inline proof points, and the animated HeroScene. Reuses the shared backend
 * routes (links to /destinations/).
 *
 * Market-test instrumentation (market-test-plan.md §3 / EXPERIMENTS §2/§5):
 *   - exposure: `wf_fast_activation_price_hook_v1` (brutalist activation + price hero).
 *   - primary CTA click: `copy_urgency_vs_trust_v1` (urgency/savings copy → CVR).
 * Both pass `experiment_id` in the shared analytics schema (additive, consent-gated).
 */
const HERO_EXPERIMENT_ID = "wf_fast_activation_price_hook_v1";
const COPY_EXPERIMENT_ID = "copy_urgency_vs_trust_v1";

export default function Hero({ fromPrice }: { fromPrice?: string }) {
  const { t } = useTranslations();

  // Brutalist price-hook hero exposure (best-effort; `track` dedupes + is
  // consent-gated). market-test-plan.md §3 (Design system / Above-the-fold).
  React.useEffect(() => {
    try {
      track({
        event_name: "hero_variant_viewed",
        event_id: `hero_variant_viewed:${HERO_EXPERIMENT_ID}`,
        experiment_id: HERO_EXPERIMENT_ID,
        variant_id: "brutalist_price_hook",
      });
    } catch {
      /* analytics is best-effort */
    }
  }, []);

  const onPrimaryCta = () => {
    try {
      track({
        event_name: "hero_cta_clicked",
        event_id: `hero_cta_clicked:${COPY_EXPERIMENT_ID}:${Date.now()}`,
        experiment_id: COPY_EXPERIMENT_ID,
        variant_id: "urgency",
      });
    } catch {
      /* analytics is best-effort */
    }
  };

  return (
    <section className="relative border-b-2 border-foreground overflow-hidden">
      <div className="container mx-auto px-4 py-14 lg:py-20 grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <span className="strike-eyebrow inline-flex items-center gap-2 border-2 border-foreground bg-warning text-warning-foreground px-3 py-1 shadow-[3px_3px_0_0_hsl(var(--foreground))]">
            <Zap className="w-3.5 h-3.5" /> {t("cobra.hero.eyebrow", "Prepaid travel eSIM")}
          </span>

          <h1 className="display-xl mt-5 text-4xl sm:text-5xl lg:text-6xl">
            {t("cobra.hero.title", "Land. Scan. Strike online.")}
          </h1>

          <p className="mt-5 text-base sm:text-lg text-foreground/80 max-w-xl font-medium">
            {t(
              "cobra.hero.subtitle",
              "Buy a data plan, scan the QR code, and get connected in minutes across 180+ countries. No SIM swap, no roaming bills.",
            )}
          </p>

          {fromPrice && (
            <p className="mt-5 inline-flex items-center gap-2 border-2 border-foreground bg-card text-foreground px-3 py-1.5 font-bold uppercase tracking-tight text-sm shadow-[3px_3px_0_0_hsl(var(--foreground))]">
              <Tag className="w-4 h-4 text-primary" />
              {t("cobra.hero.priceHook", "Plans from {price} · instant QR delivery").replace("{price}", fromPrice)}
            </p>
          )}

          <div className="mt-7 flex flex-wrap gap-3">
            <LocaleLink
              href="/destinations/"
              onClick={onPrimaryCta}
              className="inline-flex items-center gap-2 border-2 border-foreground bg-primary text-primary-foreground font-bold uppercase tracking-tight px-6 h-12 shadow-[4px_4px_0_0_hsl(var(--foreground))] transition-transform hover:-translate-x-0.5 hover:-translate-y-0.5 active:translate-x-0 active:translate-y-0 active:scale-[0.98]"
            >
              {t("cobra.hero.ctaPrimary", "Get your eSIM")} <ArrowRight className="w-4 h-4" />
            </LocaleLink>
            <a
              href="#how"
              className="inline-flex items-center gap-2 border-2 border-foreground bg-card text-foreground font-bold uppercase tracking-tight px-6 h-12 shadow-[4px_4px_0_0_hsl(var(--foreground))] transition-transform hover:-translate-x-0.5 hover:-translate-y-0.5 active:translate-x-0 active:translate-y-0 active:scale-[0.98]"
            >
              {t("cobra.hero.ctaSecondary", "See how it works")}
            </a>
          </div>

          <ul className="mt-7 flex flex-wrap gap-x-5 gap-y-2 text-sm font-bold">
            <li className="inline-flex items-center gap-1.5">
              <Zap className="w-4 h-4 text-primary" />
              {t("cobra.hero.proof1", "Instant QR delivery")}
            </li>
            <li className="inline-flex items-center gap-1.5">
              <Globe className="w-4 h-4 text-primary" />
              {t("cobra.hero.proof2", "180+ countries")}
            </li>
            <li className="inline-flex items-center gap-1.5">
              <ShieldCheck className="w-4 h-4 text-primary" />
              {t("cobra.hero.proof3", "Keep your home number")}
            </li>
          </ul>
        </div>

        <div className="hero-anim">
          <HeroScene fromPrice={fromPrice} />
        </div>
      </div>
    </section>
  );
}
